<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';
import type { SearchSuggestion } from '@/layouts/data/headerConfig';
import { searchSuggestions } from '@/layouts/data/headerConfig';

const router = useRouter();
const searchTerm = ref('');

const filteredSuggestions = computed(() => {
  const normalized = searchTerm.value.trim().toLowerCase();
  if (!normalized) {
    return searchSuggestions;
  }

  return searchSuggestions.filter((item) => {
    return (
      item.title.toLowerCase().includes(normalized) ||
      item.subtext.toLowerCase().includes(normalized)
    );
  });
});

function goToSuggestion(item: SearchSuggestion) {
  router.push(item.href);
  searchTerm.value = '';
}

function handleSearch() {
  const nextSuggestion = filteredSuggestions.value[0];
  if (nextSuggestion) {
    router.push(nextSuggestion.href);
    searchTerm.value = '';
  }
}
</script>

<template>
  <v-menu :close-on-content-click="false">
    <template #activator="{ props }">
      <v-btn
        icon
        variant="text"
        color="primary"
        v-bind="props"
      >
        <Icon
          icon="solar:magnifier-linear"
          height="20"
        />
      </v-btn>
    </template>

    <v-sheet
      width="360"
      elevation="10"
      rounded="md"
    >
      <div class="d-flex align-center justify-space-between pa-5">
        <v-text-field
          v-model="searchTerm"
          placeholder="Rechercher"
          color="primary"
          density="compact"
          variant="outlined"
          hide-details
          class="search-input"
          prepend-inner-icon="solar:magnifier-linear"
          @keyup.enter="handleSearch"
        />
        <v-btn
          icon
          variant="text"
          size="small"
          @click="searchTerm = ''"
        >
          <Icon
            icon="solar:close-circle-line-duotone"
            height="20"
          />
        </v-btn>
      </div>
      <v-divider />
      <h6 class="text-h6 mt-4 px-5 pb-4">
        Quick page links
      </h6>
      <PerfectScrollbar
        class="suggestions-scroll"
        style="height: 300px"
      >
        <v-list
          class="pt-0 pb-5"
          lines="two"
        >
          <v-list-item
            v-for="item in filteredSuggestions"
            :key="item.title"
            :to="item.href"
            color="primary"
            class="px-5 py-2 suggestion"
            @click="goToSuggestion(item)"
          >
            <div class="suggestion-content">
              <span class="text-subtitle-1 font-weight-medium">{{ item.title }}</span>
              <span class="text-subtitle-2 text--secondary">{{ item.subtext }}</span>
            </div>
          </v-list-item>
          <v-list-item
            v-if="!filteredSuggestions.length"
            class="px-5 py-3"
          >
            <span class="text-subtitle-2 text-disabled">Aucun résultat</span>
          </v-list-item>
        </v-list>
      </PerfectScrollbar>
    </v-sheet>
  </v-menu>
</template>

<style lang="scss" scoped>
.search-input {
  width: 100%;
}

.suggestion-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.suggestion {
  cursor: pointer;
}
</style>
